import { useTranslation } from "react-i18next";

import { useDemoTransactions } from "@/entities/demo";
import { formatDateTime, formatMoney, moneySign, staggerIndex } from "@/shared/lib";
import { Button } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * История операций демо-счёта (доработки §3.2).
 *
 * Отдельная строка, а не `TransactionRow` реального лога: у демо свой набор
 * типов (`DemoTransactionType`), и переиспользовать компонент, ключи которого
 * подставляются из `wallet.txType.*`, значило бы либо смешать словари, либо
 * получить на экране необработанный код операции.
 *
 * Фильтров по типу здесь нет: типов пять, операций у одного человека — десятки,
 * и панель фильтров занимала бы больше места, чем экономила.
 */
export function DemoTransactionsPage() {
  const { t } = useTranslation();
  const { data, isPending, isError, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useDemoTransactions();

  const items = data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("demo.history.title")} subtitle={t("demo.history.subtitle")} />

      {isPending ? (
        <ScreenSkeleton blocks={[92, 92, 92]} />
      ) : isError ? (
        <ScreenState
          tone="loss"
          icon="warningFill"
          title={t("states.error.title")}
          body={t("states.error.body")}
          action={{ label: t("states.error.action"), onClick: () => void refetch() }}
        />
      ) : items.length === 0 ? (
        <ScreenState
          tone="info"
          icon="trayFill"
          title={t("demo.history.empty.title")}
          body={t("demo.history.empty.body")}
        />
      ) : (
        <>
          <ul className="flex flex-col gap-3">
            {items.map((item, index) => {
              const sign = moneySign(item.amount);
              const tone =
                sign < 0
                  ? "text-status-loss"
                  : sign > 0
                    ? "text-status-profit"
                    : "text-text-secondary";

              return (
                <li key={item.id} className="stagger" style={staggerIndex(index)}>
                  <div className="flex items-start justify-between gap-3 rounded-2xl border border-border-subtle bg-background-elevated p-4">
                    <div className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-label font-semibold text-text-primary">
                        {t(`demo.txType.${item.type}`)}
                      </span>
                      {item.comment ? (
                        <span className="truncate text-caption font-normal text-text-secondary">
                          {item.comment}
                        </span>
                      ) : null}
                      <span className="text-caption font-normal text-text-tertiary">
                        {formatDateTime(item.created_at)}
                      </span>
                    </div>

                    <div className="flex shrink-0 flex-col items-end gap-0.5">
                      <span className={`text-label font-semibold ${tone}`}>
                        {formatMoney(item.amount, { sign: "always" })}
                      </span>
                      <span className="text-caption font-normal text-text-tertiary">
                        {formatMoney(item.balance_after)}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>

          {hasNextPage ? (
            <Button
              size="cta"
              variant="ghost"
              block
              disabled={isFetchingNextPage}
              onClick={() => void fetchNextPage()}
            >
              {t("common.loadMore")}
            </Button>
          ) : null}
        </>
      )}
    </div>
  );
}
